/**
 * Services-tier plants page — the customer's groups as the entry point into a
 * service project, so a crew manager picks the plant they are scheduling work
 * for and lands in that plant's sites. Same grid the customer tier shows, with
 * the project's pricing and billing shortcuts alongside it.
 *
 * Contains:
 * - PlantsGridView — routed page for /services/customer/:customerId/project/:projectId
 */
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Button, CircularProgress, Stack } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import { selectRowMeta } from '@sophocles/data-grids/components/mobile/cardSections';
import useCustomer from '@sophocles/hooks/useCustomer';
import useSiteGroupTerm from '@sophocles/hooks/useSiteGroupTerm';
import { FetchErrorState } from '@sophocles/views/services/components';
import SiteGroupGrid from '@sophocles/views/siteGroups/components/SiteGroupGrid';

const actionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  borderWidth: 2,
  '&:hover': { borderWidth: 2 },
};

/**
 * The job-progress facts the job-site-groups descriptor adds to a plant row.
 *
 * These are the plant's own desktop columns; a phone has no columns, so the
 * card restates them as metadata. Each entry is matched against the row by
 * field name and contributes a line only when the row actually carries it, so
 * a plant served by a descriptor without job columns shows its identity and
 * status alone rather than three empty labels.
 */
const JOB_PROGRESS_FIELDS = [
  { field: 'pending_jobs', labelKey: 'projectServices.plantList.columns.pendingJobs', fallback: 'Pending Jobs' },
  { field: 'completed_jobs', labelKey: 'projectServices.plantList.columns.completedJobs', fallback: 'Completed Jobs' },
  { field: 'not_started', labelKey: 'projectServices.plantList.columns.notStarted', fallback: 'Not Started' },
];

function PlantsGridView(props) {
  const { t } = useTranslation();

  const {
    permissions: { isCrewManager, isAccounting },
  } = useUser();

  const showAdminActions = isCrewManager || isAccounting;

  const {
    match: { params },
  } = props;

  const { customerId, projectId } = params;

  // A flat route, so it seeds its own customer context rather than reading the
  // scope a Layout would provide. SiteGroupGrid takes plain JS.
  const { customer: customerMap, loading, error, reload } = useCustomer(customerId);

  // useCustomer reads a store slice shared across customers, so it reports the
  // previously selected one until the fetch lands. Withhold the customer until
  // the store id matches the route, or the grid lists the wrong customer's
  // groups on the first paint after switching customers.
  const isReady = customerMap?.get?.('id', '0').toString() === String(customerId);
  const customer = useMemo(() => (isReady ? customerMap.toJS() : null), [isReady, customerMap]);

  // Customer-facing site-group term ("Plants"/"Groups") for the page title,
  // resolved from the fetched customer (flat route — no scope Layout).
  const siteGroupTerm = useSiteGroupTerm(customer);

  // Rows lead into the project's sites for that plant, not the customer-tier
  // structures grid this component defaults to.
  const siteGroupPath = useCallback(
    row => `/services/customer/${customerId}/project/${projectId}/sitegroup/${row.id}/`,
    [customerId, projectId],
  );

  const getMobileSections = useCallback(
    row => ({
      meta: selectRowMeta(
        row,
        JOB_PROGRESS_FIELDS.map(({ field, labelKey, fallback }) => ({ field, label: t(labelKey, fallback) })),
      ),
    }),
    [t],
  );

  // Virtual groups aggregate sites owned by other plants — scheduling work
  // against one would double-count, so the services tier lists real plants only.
  // No health stat cards or create/upload affordances, and the site-status
  // chip lives behind the More popover: a crew manager picking a plant acts on
  // the job-progress columns, not fleet-health tooling.
  const extension = useMemo(
    () => ({
      params: { is_virtual: false },
      statsHeader: null,
      demoteBaseQuickFilters: true,
      hideWriteActions: true,
      mobile: { getSections: getMobileSections },
    }),
    [getMobileSections],
  );

  return (
    <AppNav
      flex
      loading={loading || (!isReady && !error)}
      page={`services_projects_${customerId}_plants`}
      notFullHeight
      {...props}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', px: 2, pb: 1 }}>
        {/* Bare term title: the customer already names itself in the
            breadcrumb trail, and TitleBar appends the title as the
            current-page crumb — a "{customer} Plants" title would put the
            customer in the trail twice. */}
        <TitleBar title={loading && !customer ? <CircularProgress /> : siteGroupTerm.plural} />
        {showAdminActions && (
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SettingsIcon />}
              component={Link}
              to={`/services/customer/${customerId}/prices/`}
              sx={actionButtonSx}
            >
              {t('projectServices.plantList.manageServices')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AttachMoneyIcon />}
              component={Link}
              to="/services/reporting"
              sx={actionButtonSx}
            >
              {t('projectServices.plantList.billing')}
            </Button>
          </Stack>
        )}
        {error ? (
          <FetchErrorState
            message={t('projectServices.plantList.toast.failedToFetchCustomer', 'Failed to fetch customer')}
            onRetry={reload}
          />
        ) : (
          customer && (
            <SiteGroupGrid
              customer={customer}
              persistScope="servicesPlantsGrid"
              basePath="job-site-groups"
              siteGroupPath={siteGroupPath}
              extension={extension}
            />
          )
        )}
      </Box>
    </AppNav>
  );
}

export default PlantsGridView;
